<template>
  <div ref="tabsRoot" class="equipment-details-tabs">
    <div v-if="(equipment.intakes?.length || 0) > 1 || (equipment.seals?.length || 0) > 1" class="equipment-item" data-cy="ai-tandem-assembly">
      <div v-if="(equipment.intakes?.length || 0) > 1">
        <strong>Intake assembly (top to bottom)</strong>
        <ol><li v-for="(part, index) in equipment.intakes" :key="index">{{ part.model }} — {{ part.shaftType }}</li></ol>
      </div>
      <div v-if="(equipment.seals?.length || 0) > 1">
        <strong>Seal assembly (top to bottom)</strong>
        <ol><li v-for="(part, index) in equipment.seals" :key="index">{{ part.model }} — {{ part.shaftType }}</li></ol>
      </div>
    </div>
    <TabView scrollable>
      <!-- Pump Tab -->
      <TabPanel>
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'pump']" class="mr-2" />
            <span>Pump</span>
          </div>
        </template>
        <div
          v-if="equipment.pumps && equipment.pumps.length > 0"
          class="equipment-tab-content"
        >
          <div
            v-for="(pump, index) in equipment.pumps"
            :key="index"
            class="equipment-item"
          >
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{ pump.manufacturer || "—" }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Series/Model:</span>
              <span class="field-value">{{ pump.seriesModel || "—" }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Shaft Type:</span>
              <span class="field-value">{{ pump.shaftType || "—" }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Total Stages:</span>
              <span class="field-value">{{ pump.totalStages || "—" }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Length:</span>
              <span class="field-value">{{
                pump.lengthFeet ? `${pump.lengthFeet.toFixed(2)} ft` : "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Weight:</span>
              <span class="field-value">{{
                pump.weightPounds
                  ? `${pump.weightPounds.toLocaleString()} lbs`
                  : "—"
              }}</span>
            </div>
            <div v-if="pump.nominalOutsideDiameter" class="equipment-field">
              <span class="field-label">OD:</span>
              <span class="field-value"
                >{{ pump.nominalOutsideDiameter.toFixed(3) }}"</span
              >
            </div>
            <div v-if="pump.minRate || pump.maxRate" class="equipment-field">
              <span class="field-label">Rate Range:</span>
              <span class="field-value"
                >{{ pump.minRate || 0 }} - {{ pump.maxRate || 0 }} BPD</span
              >
            </div>
            <div v-if="pump.bepRate" class="equipment-field">
              <span class="field-label">BEP Rate:</span>
              <span class="field-value">{{ pump.bepRate }} BPD</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">In Stock:</span>
              <span class="field-value">
                <Badge v-if="pump.inStock" value="Yes" severity="success" />
                <Badge v-else value="No" severity="warning" />
              </span>
            </div>
            <div v-if="pump.quantityAvailable" class="equipment-field">
              <span class="field-label">Quantity Available:</span>
              <span class="field-value">{{ pump.quantityAvailable }}</span>
            </div>
            <div v-if="pump.leadTimeDays" class="equipment-field">
              <span class="field-label">Lead Time:</span>
              <span class="field-value">{{ pump.leadTimeDays }} days</span>
            </div>
            <div v-if="pump.erpMaterialNumber" class="equipment-field">
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{ pump.erpMaterialNumber }}</span>
            </div>
          </div>
        </div>
        <div v-else class="no-equipment">No pump selected</div>
      </TabPanel>

      <!-- Motor Tab -->
      <TabPanel>
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'bolt']" class="mr-2" />
            <span>Motor</span>
          </div>
        </template>
        <div v-if="equipment.motor" class="equipment-tab-content">
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.motor.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Series/Model:</span>
              <span class="field-value">{{
                equipment.motor.seriesModel || "—"
              }}</span>
            </div>
            <div v-if="equipment.motor.series" class="equipment-field">
              <span class="field-label">Series:</span>
              <span class="field-value">{{ equipment.motor.series }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Rated HP:</span>
              <span class="field-value">{{
                equipment.motor.ratedHP?.toFixed(2) || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Estimated Load HP:</span>
              <span class="field-value">{{
                equipment.motor.estimatedLoadHP?.toFixed(2) || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Load Ratio:</span>
              <span class="field-value">
                {{ formatMotorLoadRatio(equipment.motor) }}
              </span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Voltage:</span>
              <span class="field-value"
                >{{ equipment.motor.voltage || "—" }}V</span
              >
            </div>
            <div v-if="equipment.motor.lengthFeet" class="equipment-field">
              <span class="field-label">Length:</span>
              <span class="field-value"
                >{{ equipment.motor.lengthFeet.toFixed(2) }} ft</span
              >
            </div>
            <div v-if="equipment.motor.outsideDiameter" class="equipment-field">
              <span class="field-label">OD:</span>
              <span class="field-value"
                >{{ equipment.motor.outsideDiameter.toFixed(3) }}"</span
              >
            </div>
            <div class="equipment-field">
              <span class="field-label">In Stock:</span>
              <span class="field-value">
                <Badge
                  v-if="equipment.motor.inStock"
                  value="Yes"
                  severity="success"
                />
                <Badge v-else value="No" severity="warning" />
              </span>
            </div>
            <div
              v-if="equipment.motor.quantityAvailable"
              class="equipment-field"
            >
              <span class="field-label">Quantity Available:</span>
              <span class="field-value">{{
                equipment.motor.quantityAvailable
              }}</span>
            </div>
            <div v-if="equipment.motor.leadTimeDays" class="equipment-field">
              <span class="field-label">Lead Time:</span>
              <span class="field-value"
                >{{ equipment.motor.leadTimeDays }} days</span
              >
            </div>
            <div
              v-if="equipment.motor.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.motor.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <div v-else class="no-equipment">No motor selected</div>
      </TabPanel>

      <!-- Intake Tab -->
      <TabPanel>
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'filter']" class="mr-2" />
            <span>Intake</span>
          </div>
        </template>
        <div
          v-if="equipment.intake && Object.keys(equipment.intake).length > 0"
          class="equipment-tab-content"
        >
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.intake.manufacturer || "—"
              }}</span>
            </div>
            <div v-if="equipment.intake.model" class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{ equipment.intake.model }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Type:</span>
              <span class="field-value">{{
                equipment.intake.type || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Max Flow Rate:</span>
              <span class="field-value"
                >{{
                  equipment.intake.maxFlowRate?.toLocaleString() || "—"
                }}
                BPD</span
              >
            </div>
            <div class="equipment-field">
              <span class="field-label">Gas Handling:</span>
              <span class="field-value">
                <Badge
                  v-if="equipment.intake.gasHandling"
                  value="Yes"
                  severity="success"
                />
                <Badge v-else value="No" severity="secondary" />
              </span>
            </div>
            <div class="equipment-field">
              <span class="field-label">In Stock:</span>
              <span class="field-value">
                <Badge
                  v-if="equipment.intake.inStock"
                  value="Yes"
                  severity="success"
                />
                <Badge v-else value="No" severity="warning" />
              </span>
            </div>
            <div
              v-if="equipment.intake.quantityAvailable"
              class="equipment-field"
            >
              <span class="field-label">Quantity Available:</span>
              <span class="field-value">{{
                equipment.intake.quantityAvailable
              }}</span>
            </div>
            <div v-if="equipment.intake.leadTimeDays" class="equipment-field">
              <span class="field-label">Lead Time:</span>
              <span class="field-value"
                >{{ equipment.intake.leadTimeDays }} days</span
              >
            </div>
            <div
              v-if="equipment.intake.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.intake.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <div v-else class="no-equipment">No intake selected</div>
      </TabPanel>

      <!-- Cable Tab -->
      <TabPanel>
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'plug']" class="mr-2" />
            <span>Cable</span>
          </div>
        </template>
        <div
          v-if="equipment.cable && Object.keys(equipment.cable).length > 0"
          class="equipment-tab-content"
        >
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.cable.manufacturer || "—"
              }}</span>
            </div>
            <div v-if="equipment.cable.model" class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{ equipment.cable.model }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Gauge:</span>
              <span class="field-value">{{
                equipment.cable.gauge || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Length:</span>
              <span class="field-value"
                >{{
                  equipment.cable.lengthFeet?.toLocaleString() || "—"
                }}
                ft</span
              >
            </div>
            <div class="equipment-field">
              <span class="field-label">Voltage Rating:</span>
              <span class="field-value"
                >{{ equipment.cable.voltageRating || "—" }}V</span
              >
            </div>
            <div v-if="equipment.cable.jacketType" class="equipment-field">
              <span class="field-label">Jacket Type:</span>
              <span class="field-value">{{ equipment.cable.jacketType }}</span>
            </div>
            <div v-if="equipment.cable.conductorCount" class="equipment-field">
              <span class="field-label">Conductor Count:</span>
              <span class="field-value">{{
                equipment.cable.conductorCount
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">In Stock:</span>
              <span class="field-value">
                <Badge
                  v-if="equipment.cable.inStock"
                  value="Yes"
                  severity="success"
                />
                <Badge v-else value="No" severity="warning" />
              </span>
            </div>
            <div
              v-if="equipment.cable.quantityAvailable"
              class="equipment-field"
            >
              <span class="field-label">Quantity Available:</span>
              <span class="field-value">{{
                equipment.cable.quantityAvailable
              }}</span>
            </div>
            <div v-if="equipment.cable.leadTimeDays" class="equipment-field">
              <span class="field-label">Lead Time:</span>
              <span class="field-value"
                >{{ equipment.cable.leadTimeDays }} days</span
              >
            </div>
            <div
              v-if="equipment.cable.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.cable.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <div v-else class="no-equipment">No cable selected</div>
      </TabPanel>

      <!-- Seal Tab -->
      <TabPanel v-if="hasComponent(equipment.seal)">
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'shield-halved']" class="mr-2" />
            <span>Seal</span>
          </div>
        </template>
        <div class="equipment-tab-content">
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.seal?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{
                equipment.seal?.model || "—"
              }}</span>
            </div>
            <div v-if="equipment.seal?.bearingType" class="equipment-field">
              <span class="field-label">Bearing Type:</span>
              <span class="field-value">{{ equipment.seal.bearingType }}</span>
            </div>
            <div v-if="equipment.seal?.maxTemperatureF" class="equipment-field">
              <span class="field-label">Max Temperature:</span>
              <span class="field-value"
                >{{ equipment.seal.maxTemperatureF }}°F</span
              >
            </div>
            <div
              v-if="equipment.seal?.standardBearingRatingPounds"
              class="equipment-field"
            >
              <span class="field-label">Std Bearing Rating:</span>
              <span class="field-value"
                >{{
                  equipment.seal.standardBearingRatingPounds.toLocaleString()
                }}
                lbs</span
              >
            </div>
            <div
              v-if="equipment.seal?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.seal.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
      </TabPanel>

      <!-- Sensor Tab -->
      <TabPanel v-if="hasComponent(equipment.sensor) || issueFor('sensor')">
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'gauge']" class="mr-2" />
            <span>Sensor</span>
          </div>
        </template>
        <div
          v-if="hasComponent(equipment.sensor)"
          class="equipment-tab-content"
        >
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.sensor?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{
                equipment.sensor?.model || "—"
              }}</span>
            </div>
            <div v-if="equipment.sensor?.channels" class="equipment-field">
              <span class="field-label">Channels:</span>
              <span class="field-value">{{ equipment.sensor.channels }}</span>
            </div>
            <div
              v-if="equipment.sensor?.maxIntakeTemperatureF"
              class="equipment-field"
            >
              <span class="field-label">Max Intake Temp:</span>
              <span class="field-value"
                >{{ equipment.sensor.maxIntakeTemperatureF }}°F</span
              >
            </div>
            <div
              v-if="equipment.sensor?.maxIntakePressurePsi"
              class="equipment-field"
            >
              <span class="field-label">Max Intake Pressure:</span>
              <span class="field-value"
                >{{
                  equipment.sensor.maxIntakePressurePsi.toLocaleString()
                }}
                PSI</span
              >
            </div>
            <div
              v-if="equipment.sensor?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.sensor.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <EquipmentIssueDetails
          v-else-if="issueFor('sensor')"
          :issue="issueFor('sensor')!"
        />
      </TabPanel>

      <!-- MLE Tab -->
      <TabPanel v-if="hasComponent(equipment.mle) || issueFor('mle')">
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'plug']" class="mr-2" />
            <span>MLE</span>
          </div>
        </template>
        <div v-if="hasComponent(equipment.mle)" class="equipment-tab-content">
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.mle?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{ equipment.mle?.model || "—" }}</span>
            </div>
            <div v-if="equipment.mle?.family" class="equipment-field">
              <span class="field-label">Family:</span>
              <span class="field-value">{{ equipment.mle.family }}</span>
            </div>
            <div v-if="equipment.mle?.gauge" class="equipment-field">
              <span class="field-label">Gauge:</span>
              <span class="field-value">{{ equipment.mle.gauge }}</span>
            </div>
            <div
              v-if="equipment.mle?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.mle.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <EquipmentIssueDetails
          v-else-if="issueFor('mle')"
          :issue="issueFor('mle')!"
        />
      </TabPanel>

      <!-- Surface Cable Tab -->
      <TabPanel
        v-if="hasComponent(equipment.surfaceCable) || issueFor('surface_cable')"
      >
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'plug']" class="mr-2" />
            <span>Surface Cable</span>
          </div>
        </template>
        <div
          v-if="hasComponent(equipment.surfaceCable)"
          class="equipment-tab-content"
        >
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.surfaceCable?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{
                equipment.surfaceCable?.model || "—"
              }}</span>
            </div>
            <div v-if="equipment.surfaceCable?.gauge" class="equipment-field">
              <span class="field-label">Gauge:</span>
              <span class="field-value">{{
                equipment.surfaceCable.gauge
              }}</span>
            </div>
            <div
              v-if="equipment.surfaceCable?.voltageRating"
              class="equipment-field"
            >
              <span class="field-label">Voltage Rating:</span>
              <span class="field-value"
                >{{ equipment.surfaceCable.voltageRating }}V</span
              >
            </div>
            <div
              v-if="equipment.surfaceCable?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.surfaceCable.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <EquipmentIssueDetails
          v-else-if="issueFor('surface_cable')"
          :issue="issueFor('surface_cable')!"
        />
      </TabPanel>

      <!-- VSD Tab -->
      <TabPanel v-if="hasComponent(equipment.vsd) || issueFor('vsd')">
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'sliders']" class="mr-2" />
            <span>VSD</span>
          </div>
        </template>
        <div v-if="hasComponent(equipment.vsd)" class="equipment-tab-content">
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.vsd?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{ equipment.vsd?.model || "—" }}</span>
            </div>
            <div v-if="equipment.vsd?.type" class="equipment-field">
              <span class="field-label">Type:</span>
              <span class="field-value">{{ equipment.vsd.type }}</span>
            </div>
            <div v-if="equipment.vsd?.maxAmps" class="equipment-field">
              <span class="field-label">Max Amps:</span>
              <span class="field-value">{{ equipment.vsd.maxAmps }} A</span>
            </div>
            <div v-if="equipment.vsd?.kva" class="equipment-field">
              <span class="field-label">kVA:</span>
              <span class="field-value">{{ equipment.vsd.kva }}</span>
            </div>
            <div
              v-if="equipment.vsd?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.vsd.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <EquipmentIssueDetails
          v-else-if="issueFor('vsd')"
          :issue="issueFor('vsd')!"
        />
      </TabPanel>

      <!-- Switchboard Tab -->
      <TabPanel
        v-if="hasComponent(equipment.switchboard) || issueFor('switchboard')"
      >
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'server']" class="mr-2" />
            <span>Switchboard</span>
          </div>
        </template>
        <div
          v-if="hasComponent(equipment.switchboard)"
          class="equipment-tab-content"
        >
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.switchboard?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{
                equipment.switchboard?.model || "—"
              }}</span>
            </div>
            <div
              v-if="equipment.switchboard?.enclosure"
              class="equipment-field"
            >
              <span class="field-label">Enclosure:</span>
              <span class="field-value">{{
                equipment.switchboard.enclosure
              }}</span>
            </div>
            <div v-if="equipment.switchboard?.amps" class="equipment-field">
              <span class="field-label">Amps:</span>
              <span class="field-value"
                >{{ equipment.switchboard.amps }} A</span
              >
            </div>
            <div v-if="equipment.switchboard?.kv" class="equipment-field">
              <span class="field-label">kV:</span>
              <span class="field-value">{{ equipment.switchboard.kv }}</span>
            </div>
            <div
              v-if="equipment.switchboard?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.switchboard.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <EquipmentIssueDetails
          v-else-if="issueFor('switchboard')"
          :issue="issueFor('switchboard')!"
        />
      </TabPanel>

      <!-- Step-Up Transformer Tab -->
      <TabPanel
        v-if="
          hasComponent(equipment.stepUpTransformer) ||
          issueFor('step_up_transformer')
        "
      >
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'transformer-bolt']" class="mr-2" />
            <span>SUT</span>
          </div>
        </template>
        <div
          v-if="hasComponent(equipment.stepUpTransformer)"
          class="equipment-tab-content"
        >
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.stepUpTransformer?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{
                equipment.stepUpTransformer?.model || "—"
              }}</span>
            </div>
            <div
              v-if="equipment.stepUpTransformer?.type"
              class="equipment-field"
            >
              <span class="field-label">Type:</span>
              <span class="field-value">{{
                equipment.stepUpTransformer.type
              }}</span>
            </div>
            <div
              v-if="equipment.stepUpTransformer?.kva"
              class="equipment-field"
            >
              <span class="field-label">kVA:</span>
              <span class="field-value">{{
                equipment.stepUpTransformer.kva
              }}</span>
            </div>
            <div
              v-if="equipment.stepUpTransformer?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.stepUpTransformer.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <EquipmentIssueDetails
          v-else-if="issueFor('step_up_transformer')"
          :issue="issueFor('step_up_transformer')!"
        />
      </TabPanel>

      <!-- Step-Down Transformer Tab -->
      <TabPanel
        v-if="
          hasComponent(equipment.stepDownTransformer) ||
          issueFor('step_down_transformer')
        "
      >
        <template #header>
          <div class="equipment-tab-header">
            <icon :icon="['fas', 'transformer-bolt']" class="mr-2" />
            <span>SDT</span>
          </div>
        </template>
        <div
          v-if="hasComponent(equipment.stepDownTransformer)"
          class="equipment-tab-content"
        >
          <div class="equipment-item">
            <div class="equipment-field">
              <span class="field-label">Manufacturer:</span>
              <span class="field-value">{{
                equipment.stepDownTransformer?.manufacturer || "—"
              }}</span>
            </div>
            <div class="equipment-field">
              <span class="field-label">Model:</span>
              <span class="field-value">{{
                equipment.stepDownTransformer?.model || "—"
              }}</span>
            </div>
            <div
              v-if="equipment.stepDownTransformer?.type"
              class="equipment-field"
            >
              <span class="field-label">Type:</span>
              <span class="field-value">{{
                equipment.stepDownTransformer.type
              }}</span>
            </div>
            <div
              v-if="equipment.stepDownTransformer?.kva"
              class="equipment-field"
            >
              <span class="field-label">kVA:</span>
              <span class="field-value">{{
                equipment.stepDownTransformer.kva
              }}</span>
            </div>
            <div
              v-if="equipment.stepDownTransformer?.erpMaterialNumber"
              class="equipment-field"
            >
              <span class="field-label">Material ID:</span>
              <span class="field-value">{{
                equipment.stepDownTransformer.erpMaterialNumber
              }}</span>
            </div>
          </div>
        </div>
        <EquipmentIssueDetails
          v-else-if="issueFor('step_down_transformer')"
          :issue="issueFor('step_down_transformer')!"
        />
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Badge from "primevue/badge";
import EquipmentIssueDetails from "@/components/EquipmentIssueDetails.vue";
import type {
  AIEquipmentIssue,
  AIEquipmentType
} from "@/libs/api/aiEquipmentSuggestions";

interface Equipment {
  intakes?: Array<{ model?: unknown; shaftType?: unknown }>;
  seals?: Array<{ model?: unknown; shaftType?: unknown }>;
  pumps?: Array<{
    manufacturer?: string;
    seriesModel?: string;
    model?: string;
    shaftType?: string;
    totalStages?: number;
    lengthFeet?: number;
    weightPounds?: number;
    nominalOutsideDiameter?: number;
    minRate?: number;
    bepRate?: number;
    maxRate?: number;
    inStock?: boolean;
    quantityAvailable?: number;
    leadTimeDays?: number;
    erpMaterialNumber?: string;
  }>;
  motor?: {
    manufacturer?: string;
    seriesModel?: string;
    series?: string;
    ratedHP?: number;
    estimatedLoadHP?: number;
    voltage?: number;
    lengthFeet?: number;
    outsideDiameter?: number;
    inStock?: boolean;
    quantityAvailable?: number;
    leadTimeDays?: number;
    erpMaterialNumber?: string;
  };
  intake?: {
    manufacturer?: string;
    model?: string;
    type?: string;
    maxFlowRate?: number;
    gasHandling?: boolean;
    inStock?: boolean;
    quantityAvailable?: number;
    leadTimeDays?: number;
    erpMaterialNumber?: string;
  };
  cable?: {
    manufacturer?: string;
    model?: string;
    gauge?: number;
    lengthFeet?: number;
    voltageRating?: number;
    jacketType?: string;
    conductorCount?: number;
    inStock?: boolean;
    quantityAvailable?: number;
    leadTimeDays?: number;
    erpMaterialNumber?: string;
  };
  seal?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    bearingType?: string;
    maxTemperatureF?: number;
    standardBearingRatingPounds?: number;
    highStrengthBearingRatingPounds?: number;
    erpMaterialNumber?: string;
  };
  sensor?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    channels?: number;
    maxIntakeTemperatureF?: number;
    maxIntakePressurePsi?: number;
    erpMaterialNumber?: string;
  };
  mle?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    family?: string;
    gauge?: string | number;
    erpMaterialNumber?: string;
  };
  surfaceCable?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    gauge?: string | number;
    voltageRating?: number;
    erpMaterialNumber?: string;
  };
  vsd?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    type?: string;
    maxAmps?: number;
    kva?: number;
    erpMaterialNumber?: string;
  };
  switchboard?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    enclosure?: string;
    amps?: number;
    kv?: number;
    erpMaterialNumber?: string;
  };
  stepUpTransformer?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    type?: string;
    kva?: number;
    erpMaterialNumber?: string;
  };
  stepDownTransformer?: {
    manufacturer?: string;
    model?: string;
    partNumber?: string;
    type?: string;
    kva?: number;
    erpMaterialNumber?: string;
  };
}

const props = defineProps<{
  equipment: Equipment;
  issues?: AIEquipmentIssue[];
}>();

const tabsRoot = ref<HTMLElement | null>(null);

function resetTabsScroll() {
  nextTick(() => {
    const reset = () => {
      const scrollContent = tabsRoot.value?.querySelector<HTMLElement>(
        ".p-tabview-nav-content"
      );

      if (!scrollContent) return;

      scrollContent.scrollLeft = 0;
      scrollContent.dispatchEvent(new Event("scroll", { bubbles: true }));
    };

    reset();
    window.requestAnimationFrame(reset);
  });
}

onMounted(resetTabsScroll);
watch(() => props.equipment, resetTabsScroll, {
  flush: "post",
  immediate: true
});

function hasComponent(component: Record<string, unknown> | undefined): boolean {
  return !!component && Object.keys(component).length > 0;
}

function issueFor(
  equipmentType: AIEquipmentType
): AIEquipmentIssue | undefined {
  return props.issues?.find(issue => issue.equipmentType === equipmentType);
}

function formatMotorLoadRatio(motor: Equipment["motor"]): string {
  if (!motor?.estimatedLoadHP || !motor.ratedHP) return "—";
  return `${((motor.estimatedLoadHP / motor.ratedHP) * 100).toFixed(0)}%`;
}
</script>

<style scoped>
.equipment-details-tabs {
  margin-top: 1rem;
  min-width: 0;
  --equipment-tab-selected-color: #31373f;
}

:global([data-theme="dark"]) .equipment-details-tabs {
  --equipment-tab-selected-color: #fafafa;
}

.equipment-tab-header {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  gap: 0.5rem;
  white-space: nowrap;
  color: inherit;
}

.equipment-tab-content {
  padding: 1rem 0;
}

.equipment-item {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  background: var(--surface-100);
  padding: 1rem;
  border-radius: 6px;
}

.equipment-field {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.25rem 0;
  gap: 0.75rem;
  min-width: 0;
}

.field-label {
  font-weight: 600;
  color: var(--text-color-secondary);
  font-size: 0.85rem;
  flex: 0 0 auto;
}

.field-value {
  font-weight: 500;
  color: var(--text-color);
  text-align: right;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.equipment-details-tabs :deep(.p-tabview-nav) {
  flex-wrap: nowrap;
}

.equipment-details-tabs :deep(.p-tabview-scrollable .p-tabview-nav-container) {
  display: flex;
  min-width: 0;
}

.equipment-details-tabs :deep(.p-tabview-scrollable .p-tabview-nav-content) {
  flex: 1 1 auto;
  min-width: 0;
}

.equipment-details-tabs :deep(.p-tabview-scrollable .p-tabview-nav-btn) {
  position: static;
  flex: 0 0 3rem;
  align-self: stretch;
  height: auto;
  min-height: 3rem;
  box-shadow: none;
}

.equipment-details-tabs :deep(.p-tabview-header) {
  flex: 0 0 auto;
}

.equipment-details-tabs :deep(.p-tabview-nav-link) {
  padding: 0.75rem 1rem;
  white-space: nowrap;
}

.equipment-details-tabs
  :deep(.p-tabview-header.p-highlight .p-tabview-nav-link),
.equipment-details-tabs
  :deep(.p-tabview-header.p-tabview-selected .p-tabview-nav-link),
.equipment-details-tabs
  :deep(.p-tabview-header[data-p-active="true"] .p-tabview-nav-link),
.equipment-details-tabs :deep(.p-tabview-nav-link[aria-selected="true"]),
.equipment-details-tabs :deep(.p-tabview-nav-link[tabindex="0"]) {
  background: var(--surface-25);
  color: var(--equipment-tab-selected-color) !important;
}

.equipment-details-tabs
  :deep(.p-tabview-header.p-highlight .equipment-tab-header),
.equipment-details-tabs
  :deep(.p-tabview-header.p-tabview-selected .equipment-tab-header),
.equipment-details-tabs
  :deep(.p-tabview-header[data-p-active="true"] .equipment-tab-header),
.equipment-details-tabs
  :deep(.p-tabview-nav-link[aria-selected="true"] .equipment-tab-header),
.equipment-details-tabs
  :deep(.p-tabview-nav-link[tabindex="0"] .equipment-tab-header) {
  color: var(--equipment-tab-selected-color) !important;
}

.equipment-details-tabs
  :deep(.p-tabview-header.p-highlight .equipment-tab-header > *),
.equipment-details-tabs
  :deep(.p-tabview-header.p-tabview-selected .equipment-tab-header > *),
.equipment-details-tabs
  :deep(.p-tabview-header[data-p-active="true"] .equipment-tab-header > *),
.equipment-details-tabs
  :deep(.p-tabview-nav-link[aria-selected="true"] .equipment-tab-header > *),
.equipment-details-tabs
  :deep(.p-tabview-nav-link[tabindex="0"] .equipment-tab-header > *) {
  color: var(--equipment-tab-selected-color) !important;
}

.equipment-details-tabs
  :deep(.p-tabview-nav > .p-tabview-header:first-child .p-tabview-nav-link),
.equipment-details-tabs
  :deep(.p-tabview-nav > .p-tabview-header:first-child .equipment-tab-header),
.equipment-details-tabs
  :deep(
    .p-tabview-nav > .p-tabview-header:first-child .equipment-tab-header > *
  ) {
  color: var(--equipment-tab-selected-color) !important;
}

.no-equipment {
  padding: 2rem;
  text-align: center;
  color: var(--text-color-secondary);
  font-style: italic;
}

@media (max-width: 768px) {
  .equipment-item {
    grid-template-columns: 1fr;
  }
}
</style>
